import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTournament } from '../context/TournamentContext';
import { BracketDiagram } from '../components/BracketDiagram';
import { Trophy, Medal, ChevronDown } from 'lucide-react';
import { calculateGameResult } from '../engine/scoring';
import { PartnersCard } from '../components/PartnersCard';

export function ResultsPage() {
  const navigate = useNavigate();
  const { standings, matchUps, matchUpResults, getTeam, isLoading, getGamesByMatchUp, getScoresByGame, getPlayer } = useTournament();
  const [expandedMatchUps, setExpandedMatchUps] = useState<Set<string>>(new Set());

  function toggleMatchUp(id: string) {
    setExpandedMatchUps(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (isLoading) {
    return <div className="loading-container"><div className="spinner" /></div>;
  }

  const positionIcons: Record<number, string> = {
    1: '🥇', 2: '🥈', 3: '🥉',
  };

  return (
    <div>
      <h1 className="mb-lg">
        <Trophy size={28} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
        Výsledky
      </h1>

      {/* All match-up results */}
      <div className="card mb-lg">
        <h3 style={{ marginBottom: '1rem' }}>Výsledky zápasů</h3>
        <div className="flex flex-col gap-sm">
          {matchUps.map(mu => {
            const teamA = mu.team_a_id ? getTeam(mu.team_a_id) : null;
            const teamB = mu.team_b_id ? getTeam(mu.team_b_id) : null;
            const result = matchUpResults.get(mu.id);
            const games = getGamesByMatchUp(mu.id);
            const isExpanded = expandedMatchUps.has(mu.id);

            const isLive = mu.status === 'in_progress' || (result && (result.liveTeamAPoints > 0 || result.liveTeamBPoints > 0));

            return (
              <div key={mu.id} className={`matchup-item${isExpanded ? ' expanded' : ''}`}>
                {/* Main row */}
                <div
                  className="matchup-row"
                  onClick={() => games.length > 0 && toggleMatchUp(mu.id)}
                  style={{ cursor: games.length > 0 ? 'pointer' : 'default' }}
                >
                  <span className="matchup-label">{mu.label}</span>

                  <div className="matchup-teams">
                    <span className="matchup-team" style={{ color: teamA?.color }}>
                      {teamA?.short_name ?? teamA?.name ?? 'TBD'}
                    </span>
                    <span className="matchup-score">
                      {result ? `${result.liveTeamAPoints} : ${result.liveTeamBPoints}` : '—'}
                    </span>
                    <span className="matchup-team" style={{ color: teamB?.color }}>
                      {teamB?.short_name ?? teamB?.name ?? 'TBD'}
                    </span>
                  </div>

                  <div className="matchup-meta">
                    <span className={
                      result?.isCompleted ? 'matchup-status done'
                      : isLive ? 'matchup-status live'
                      : 'matchup-status scheduled'
                    }>
                      {result?.isCompleted ? '✓ Hotovo' : isLive ? '● Live' : 'Nezačal'}
                    </span>
                    {games.length > 0 && (
                      <ChevronDown
                        size={16}
                        color="var(--text-muted)"
                        style={{
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease',
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </div>
                </div>

                {/* Expanded game detail */}
                {isExpanded && games.length > 0 && (
                  <div style={{ padding: '0 1rem 1rem 1rem', borderTop: '1px solid var(--border)' }}>
                    <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {games.map(game => {
                        const gameScores = getScoresByGame(game.id);
                        const gameResult = calculateGameResult(game, gameScores);
                        const mpState = gameResult.matchPlayState;

                        let statusText = 'A/S';
                        let statusColor = 'var(--text-muted)';

                        if (mpState.isFinished) {
                          statusText = mpState.resultText ?? 'A/S';
                          statusColor = mpState.winnerSide === 'a' ? 'var(--side-a)' : mpState.winnerSide === 'b' ? 'var(--side-b)' : 'var(--text-secondary)';
                        } else if (mpState.holesPlayed > 0) {
                          if (mpState.lead > 0) {
                            statusText = `${mpState.lead} UP`;
                            statusColor = mpState.leaderSide === 'a' ? 'var(--side-a)' : 'var(--side-b)';
                          }
                        }

                        const gameLabel = game.type === 'foursome' ? 'Foursome' : `Single ${game.game_number}`;

                        let pA = 'TBD', pB = 'TBD';
                        if (game.type === 'single') {
                          pA = game.player_a_id ? getPlayer(game.player_a_id)?.name ?? 'TBD' : 'TBD';
                          pB = game.player_b_id ? getPlayer(game.player_b_id)?.name ?? 'TBD' : 'TBD';
                        } else {
                          const a1 = game.foursome_a1_id ? getPlayer(game.foursome_a1_id)?.name : '?';
                          const a2 = game.foursome_a2_id ? getPlayer(game.foursome_a2_id)?.name : '?';
                          const b1 = game.foursome_b1_id ? getPlayer(game.foursome_b1_id)?.name : '?';
                          const b2 = game.foursome_b2_id ? getPlayer(game.foursome_b2_id)?.name : '?';
                          pA = `${a1} + ${a2}`;
                          pB = `${b1} + ${b2}`;
                        }

                        return (
                          <div
                            key={game.id}
                            className="results-game-row"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/#game-${game.id}`);
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="results-game-label">{gameLabel}</div>
                            <div className="results-game-players">
                              <span style={{ textAlign: 'right', flex: 1, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{pA}</span>
                              <span className="vs" style={{ color: 'var(--text-muted)' }}>vs</span>
                              <span style={{ textAlign: 'left', flex: 1, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{pB}</span>
                            </div>
                            <div className="results-game-status" style={{ color: statusColor }}>
                              {statusText}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Final Standings */}
      <div className="card mb-lg">
        <h3 style={{ marginBottom: '1.25rem' }}>
          <Medal size={20} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
          Konečné pořadí
        </h3>
        {standings.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {standings.map(s => {
              const isTop3 = s.position <= 3;
              const isWinner = s.position === 1;
              const medalBorder = s.position === 1
                ? 'rgba(232,146,46,0.5)'
                : s.position === 2
                  ? 'rgba(192,192,210,0.5)'
                  : s.position === 3
                    ? 'rgba(205,127,50,0.45)'
                    : 'var(--border)';
              const medalBg = s.position === 1
                ? 'linear-gradient(135deg, rgba(232,146,46,0.15), rgba(232,146,46,0.04))'
                : s.position === 2
                  ? 'linear-gradient(135deg, rgba(192,192,210,0.1), rgba(192,192,210,0.03))'
                  : s.position === 3
                    ? 'linear-gradient(135deg, rgba(205,127,50,0.1), rgba(205,127,50,0.03))'
                    : 'var(--bg-surface)';
              return (
                <div
                  key={s.teamId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: isTop3 ? '1rem 1.25rem' : '0.75rem 1.25rem',
                    background: medalBg,
                    borderRadius: 'var(--radius-md)',
                    border: `1px solid ${medalBorder}`,
                    borderLeft: `4px solid ${s.teamColor}`,
                    transition: 'all 0.2s',
                  }}
                >
                  <span style={{
                    fontFamily: 'var(--font-mono)',
                    fontWeight: 800,
                    fontSize: isTop3 ? '1.5rem' : '1.125rem',
                    minWidth: '2.25rem',
                    textAlign: 'center',
                    lineHeight: 1,
                  }}>
                    {positionIcons[s.position] ?? `${s.position}.`}
                  </span>
                  <div style={{ flex: 1 }}>
                    <span style={{
                      fontFamily: 'var(--font-heading)',
                      fontWeight: 800,
                      fontSize: isTop3 ? '1.25rem' : '1rem',
                      textTransform: 'uppercase',
                      color: isWinner ? 'var(--accent)' : 'var(--text-primary)',
                      letterSpacing: '0.02em',
                    }}>
                      {s.teamName}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state" style={{ padding: '2rem' }}>
            <p>Výsledky budou k dispozici po dokončení zápasů</p>
          </div>
        )}
      </div>

      {/* Bracket */}
      <div className="mb-lg">
        <BracketDiagram />
      </div>

      <PartnersCard />
    </div>
  );
}
